// BUG: On failed refresh, all pending requests get run repeatedly.import axios from 'axios';
import axios from 'axios';
import store from '../store';
import router from '../router';
import qs from 'qs';
const AUTH_HEADER_KEY = 'x-access-token';

// Create axios instance
const http = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  withCredentials: true,
  timeout: 120000,
  // Serialize params with object syntax
  paramsSerializer: (params) => {
    return qs.stringify(params);
  }
});
// Set defaults
http.defaults.headers.post['Content-Type'] = 'application/json';

// **************************************** ADD INTERCEPTORS ****************************************
http.interceptors.request.use(
  /** Attach access token to each API request. */
  (config) => {
    if (store.getters.isLoggedIn) {
      config.headers[AUTH_HEADER_KEY] = store.state.accessToken;
    }
    return config;
  },

  (err) => {
    return Promise.reject(err);
  }
);

http.interceptors.response.use(
  (response) => response,

  /** Handle responses with errors from API. */
  (error) => {
    if (axios.isAxiosError(error)) {
      let { config, response } = error;

      // If could not connect to server
      if (response?.status === 0) {
        response = {
          data: { message: 'Error: something unexpected happened.', error: 'Connection timeout' }
        };
        return Promise.reject(error);
      }

      // If unauthenticated, try to refresh the access token using refresh token stored in browser cookies.
      else if (response.status === 401) {
        const ROUTES_TO_IGNORE = ['login', 'refresh-token'];
        const ROUTES_TO_IGNORE_REGEX = new RegExp(ROUTES_TO_IGNORE.join('|'));
        if (!config?.url?.match(ROUTES_TO_IGNORE_REGEX)) {
          return handle401Response(error);
        }
      }

      // If unauthorized, go to unauthorized route
      else if (response.status === 403) {
        router.push('/unauthorized');
      }
    }

    return Promise.reject(error);
  }
);

/** Automatically refreshes access token on getting an unauthorized error response.*/
async function handle401Response(originalError) {
  const originalRequest = originalError.config;
  // Prevent multiple token refreshes at once
  if (!store.state.isTokenRefreshing) {
    try {
      const accessToken = await refreshAccessToken();
      runPendingRequests(accessToken, undefined);
      return http(originalRequest);
    } catch (err) {
      runPendingRequests(undefined, err);
      return Promise.reject(originalError);
    }
  }

  // Add this request to pending requests
  return new Promise((resolve, reject) => {
    pendingRequests.push((token, error) => {
      if (token) {
        resolve(token);
      } else {
        reject(error);
      }
    });
  }).then((accessToken) => {
    originalRequest.headers[AUTH_HEADER_KEY] = accessToken;
    return http(originalRequest);
  });
}

/** A list of requests waiting on some action before they are run. */
let pendingRequests = [];

function runPendingRequests(accessToken, originalError) {
  // Run all pending requests after refresh is complete.
  for (let callback of pendingRequests) {
    callback(accessToken, originalError);
  }
  // Reset pending requests
  pendingRequests = [];
}

/** Refresh the access token. */
function refreshAccessToken() {
  // Mark token state as refreshing
  store.commit('setTokenRefresh', true);

  // Retrieve new access token
  return http
    .post('/auth/refresh-token', { userId: store.state.userId })
    .then((res) => {
      // Store new access token
      const { accessToken, user } = res.data;
      store.commit('authUser', { accessToken, userId: user.id });
      store.commit('storeUser', { user, roles: user.roles });
      // Mark token state as done refreshing
      store.commit('setTokenRefresh', false);

      return accessToken;
    })
    .catch((err) => {
      store.commit('discardSession');
      // Mark token state as done refreshing
      store.commit('setTokenRefresh', false);

      /* Avoid redundant navigation error */
      if (router.currentRoute.name.toLowerCase() != 'login') {
        router.push({
          name: 'Login',
          query: { redirect: router.currentRoute.query.redirect || router.currentRoute.fullPath },
          params: {
            passedErrors: ['Your session has expired.']
          }
        });
      }

      throw err;
    });
}

export default http;
